// rxjs-extension/rxjs-helpers.ts
import { combineLatest, concat, defer, Observable, ReplaySubject, Subscription } from "rxjs";
import { filter, finalize, map, publish, tap } from "rxjs/operators";
var is = (shouldBe) => map((value) => value === shouldBe);
var ifIs = (shouldBe, then, elseValue) => map((value) => value === shouldBe ? then : elseValue || "");
var truthy = () => map((value) => !!value);
var falsy = () => map((value) => !value);
var opposite = falsy;
var ifTruthy = () => filter((value) => !!value);
var ifFalsy = () => filter((value) => !value);
var l = (...message) => tap((...args) => console.log(...message.length === 0 ? ["got here"] : message, ...args));
var debugSubscriber = (name) => ({
  next: (...args) => console.log("debug subscriber", name, "next", ...args),
  error: (err) => console.error("debug subscriber", name, "error", err),
  complete: () => console.log("debug subscriber", name, "completed")
});
var bufferTemporarily = (stoppedObs) => (obs) => {
  let buffer = [];
  return new Observable((subscriber) => {
    let stopped = false;
    const stoppedSub = stoppedObs.subscribe(
      (stoppedValue) => {
        stopped = stoppedValue;
        if (!stopped) {
          if (buffer.length)
            subscriber.next(buffer.slice(-1)[0]);
          buffer = [];
        }
      },
      (err) => subscriber.error(err),
      // dont buffer when stopped obs is completed
      () => stopped = false
    );
    const obsSub = obs.subscribe(
      (clicked) => {
        if (stopped)
          buffer.push(clicked);
        else
          subscriber.next(clicked);
      },
      (err) => subscriber.error(err),
      () => subscriber.complete()
    );
    return () => {
      obsSub.unsubscribe();
      stoppedSub.unsubscribe();
    };
  });
};
var completingShareReplay = () => {
  return (source) => {
    const repl = new ReplaySubject();
    let refCount = 0;
    const subscription = source.subscribe({
      next(value) {
        repl.next(value);
      },
      error(err) {
        repl.error(err);
      },
      complete() {
        repl.complete();
      }
    });
    return new Observable((subscriber) => {
      refCount++;
      const innerSub = repl.subscribe(subscriber);
      return () => {
        refCount--;
        innerSub.unsubscribe();
        if (refCount === 0) {
          subscription.unsubscribe();
          subscriber.complete();
          repl.complete();
        }
      };
    });
  };
};
var finalizeWithValue = (callback) => {
  return (source) => defer(() => {
    let lastValue;
    return source.pipe(
      tap((value) => lastValue = value),
      finalize(() => callback(lastValue))
    );
  });
};
function delayUntil(notifier) {
  return (source) => source.pipe(
    publish((published) => {
      const delayed = new Observable((subscriber) => {
        let buffering = true;
        const buffer = [];
        const subscription = new Subscription();
        subscription.add(
          notifier.subscribe(
            () => {
              buffer.forEach((value) => subscriber.next(value));
              subscriber.complete();
            },
            (error) => subscriber.error(error),
            () => {
              buffering = false;
              buffer.length = 0;
            }
          )
        );
        subscription.add(
          published.subscribe(
            (value) => buffering && buffer.push(value),
            (error) => subscriber.error(error)
          )
        );
        subscription.add(() => {
          buffer.length = 0;
        });
        return subscription;
      });
      return concat(delayed, published);
    })
  );
}
var either = (...obs) => combineLatest([obs]).pipe(map((obsArr) => obsArr.some((val) => !!val)));
var all = (...obs) => combineLatest([obs]).pipe(map((obsArr) => obsArr.every((val) => !!val)));
export {
  all,
  bufferTemporarily,
  completingShareReplay,
  debugSubscriber,
  delayUntil,
  either,
  falsy,
  finalizeWithValue,
  ifFalsy,
  ifIs,
  ifTruthy,
  is,
  l,
  opposite,
  truthy
};
